/**
 * Account-level usage for You › Usage and New session: one row per upstream quota (aliases of the
 * same provider account on several machines share a usage key and are shown once, never summed),
 * built only from decrypted machine reports.
 */
import type { ProviderAccountMetadata, ProviderUsage } from '@viko/protocol'
import {
  type AccountCandidate,
  type AccountChoice,
  accountBudget,
  chooseAccount,
  ingestRateLimit,
} from '@viko/scheduler'
import { accountUsable } from './provider-accounts.js'
import type { Machine, UsageWindow } from './types.js'

export interface AccountAlias {
  machineId: string
  machineName: string
  online: boolean
  account: ProviderAccountMetadata
}
export interface AccountUsageRow {
  usageKey: string
  provider: 'claude' | 'codex'
  /** Distinct labels across aliases, in machine order. */
  labels: string[]
  aliases: AccountAlias[]
  /** Newest reading across the machines that report this key. */
  usage?: ProviderUsage
  /** Machine that reported the newest reading. */
  reportedBy?: string
}

const defaultKey = (a: ProviderAccountMetadata) =>
  a.usageKey ?? (a.mode === 'legacy' ? a.provider : a.id)

const WINDOW_NAMES = new Set(['5h', '7d', '7d_opus', '7d_sonnet', 'overage'])

/** Plaintext (legacy) hubs report usage via `/api/usage` rows keyed `<usageKey>:<window>`. */
function fromHub(key: string, hub: readonly UsageWindow[]): ProviderUsage | undefined {
  const mine = hub.filter((h) => h.accountId === key && !h.id.endsWith(':limit'))
  const limit = hub.find((h) => h.accountId === key && h.id.endsWith(':limit'))
  if (!mine.length && !limit) return undefined
  const agent = (mine[0] ?? limit)!.agent === 'codex' ? 'codex' : 'claude'
  return {
    accountId: key,
    agent,
    observedAt: Math.max(...[...mine, ...(limit ? [limit] : [])].map((h) => h.observedAt ?? 0)),
    windows: mine.map((h) => {
      const name = h.id.slice(key.length + 1)
      return {
        window: (WINDOW_NAMES.has(name)
          ? name
          : 'other') as ProviderUsage['windows'][number]['window'],
        usedPct: h.usedPct,
        ...(h.resetsAt ? { resetsAt: h.resetsAt } : {}),
      }
    }),
    ...(limit
      ? { limitReached: limit.limitReached?.stated ? { resetsAt: limit.resetsAt } : {} }
      : {}),
  }
}

export function accountUsageRows(
  machines: readonly Machine[],
  hub: readonly UsageWindow[] = [],
): AccountUsageRow[] {
  const rows = new Map<string, AccountUsageRow>()
  for (const m of machines)
    for (const a of m.providerAccounts ?? []) {
      const key = defaultKey(a)
      const row = rows.get(key) ?? {
        usageKey: key,
        provider: a.provider,
        labels: [],
        aliases: [],
      }
      if (!row.labels.includes(a.label)) row.labels.push(a.label)
      row.aliases.push({ machineId: m.id, machineName: m.name, online: m.online, account: a })
      rows.set(key, row)
    }
  for (const m of machines)
    for (const u of m.usage ?? []) {
      const row = rows.get(u.accountId)
      if (!row) continue
      if (!row.usage || u.observedAt > row.usage.observedAt) {
        row.usage = u
        row.reportedBy = m.name
      }
    }
  for (const row of rows.values())
    if (!row.usage) {
      const u = fromHub(row.usageKey, hub)
      if (u) row.usage = u
    }
  // Only accounts someone can act on: enabled somewhere, or with a reading.
  return [...rows.values()]
    .filter((r) => r.usage || r.aliases.some((x) => x.account.enabled))
    .sort(
      (a, b) =>
        a.provider.localeCompare(b.provider) ||
        (b.usage?.observedAt ?? 0) - (a.usage?.observedAt ?? 0) ||
        a.usageKey.localeCompare(b.usageKey),
    )
}

/** Human window names; configured windows only, never assumed durations. */
export function windowLabel(w: ProviderUsage['windows'][number]): string {
  switch (w.window) {
    case '5h':
      return '5-hour'
    case '7d':
      return 'Weekly'
    case '7d_opus':
      return 'Weekly · Opus'
    case '7d_sonnet':
      return 'Weekly · Sonnet'
    case 'overage':
      return 'Extra usage'
    default:
      return w.windowMinutes ? `${w.windowMinutes}-minute` : 'Other'
  }
}

/**
 * New session "Auto": the same reset-aware router the hub uses, over this machine's accounts and
 * the freshest readings from every machine. The result is sent as an explicit account.
 */
export function autoAccount(
  machine: Machine,
  agent: 'claude' | 'codex',
  machines: readonly Machine[],
  now: number,
): AccountChoice {
  const rows = new Map(accountUsageRows(machines).map((r) => [r.usageKey, r]))
  const candidates: AccountCandidate[] = (machine.providerAccounts ?? [])
    .filter((a) => a.provider === agent && a.enabled)
    .map((a) => ({
      localId: a.id,
      usageKey: defaultKey(a),
      label: a.label,
      available: accountUsable(a) && !a.loginPending,
      ...(accountUsable(a) ? {} : { unavailableReason: a.readiness }),
    }))
  return chooseAccount({
    agent,
    candidates,
    budget: (key, a) => {
      const u = rows.get(key)?.usage
      return accountBudget(
        u
          ? ingestRateLimit(undefined, {
              accountId: key,
              agent: u.agent,
              observedAt: u.observedAt,
              windows: u.windows,
              ...(u.limitReached ? { limitReached: u.limitReached } : {}),
            })
          : undefined,
        { accountId: key, agent: a },
        now,
      )
    },
    load: (key) =>
      machines
        .flatMap((m) => m.providerAccounts ?? [])
        .filter((a) => defaultKey(a) === key)
        .reduce((n, a) => n + (a.activeRuns ?? 0), 0),
    now,
  })
}
